import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  usePharmacyDrugs,
  usePharmacyDrugCategories,
  useCreatePharmacySale,
  useUpdatePharmacySale,
  usePharmacySale,
  usePatients,
  useSearchPatients,
} from '../../services/httpServices/queries';
import type { PharmacyDrug } from '../../services/httpServices/pharmacyDrugService';
import type { PharmacySalePaymentMethod } from '../../services/httpServices/pharmacySaleService';
import { useDebounce } from '../../hooks/useDebounce';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { Pagination } from '../../components/ui/Pagination';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { joinFullName } from '../../utils/patientName';
import { PharmacyLayout } from '../pharmacy/PharmacyLayout';
import { printPharmacySaleReceipt } from '../../utils/thermalReceipt';
import {
  Loader2,
  AlertCircle,
  Check,
  X,
  Search,
  ArrowLeft,
  Package,
  ShoppingCart,
  User,
  Plus,
  Minus,
  Trash2,
  CheckCircle2,
  Banknote,
  ChevronDown,
  Pencil,
  Printer,
} from 'lucide-react';

const ITEMS_PER_PAGE = 20;

/**
 * Where the in-progress (unsaved) counter-sale draft is temporarily stored so
 * the cart survives a page reload. It is cleared as soon as the sale completes.
 */
const SALE_DRAFT_STORAGE_KEY = 'pharmacy-sale-draft-v1';

const PAYMENT_METHODS: { value: PharmacySalePaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI / Mobile' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance', label: 'Insurance' },
];

interface CartLine {
  drug: PharmacyDrug;
  quantity: number;
  unitPrice: number;
}

interface PaymentLine {
  method: PharmacySalePaymentMethod;
  amount: string;
  reference: string;
}

interface SelectedPatient {
  id: string;
  name: string;
  registrationNumber?: string;
  mobileNumber?: string;
}

interface SaleDraft {
  cart: CartLine[];
  saleDiscountPercent: string;
  payments: PaymentLine[];
  notes: string;
  patientId: string;
  selectedPatient: SelectedPatient | null;
  customerName: string;
  customerPhone: string;
  customerMode: 'existing' | 'walk_in';
}

const loadSaleDraft = (): SaleDraft | null => {
  try {
    const raw = localStorage.getItem(SALE_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as SaleDraft) : null;
  } catch {
    return null;
  }
};

const saveSaleDraft = (draft: SaleDraft) => {
  try {
    localStorage.setItem(SALE_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* ignore storage failures (quota / disabled) */
  }
};

const clearSaleDraft = () => {
  try {
    localStorage.removeItem(SALE_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore storage failures */
  }
};

const money = (value: number) => `৳${(Number.isFinite(value) ? value : 0).toLocaleString()}`;
const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function PharmacySaleForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: saleId } = useParams<{ id: string }>();
  const isEdit = Boolean(saleId);

  /* ── URL-backed catalog filters (survive reload / shareable) ── */
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  /* ── restore the unsaved draft (new sales only) from localStorage ── */
  const [initialDraft] = useState<SaleDraft | null>(() => (isEdit ? null : loadSaleDraft()));
  // Set once the sale is completed so the persistence effect stops re-saving it.
  const draftClearedRef = useRef(false);

  /* ── customer ── */
  const [patientId, setPatientId] = useState(() => initialDraft?.patientId || '');
  const [selectedPatient, setSelectedPatient] = useState<SelectedPatient | null>(
    () => initialDraft?.selectedPatient || null,
  );
  const [customerName, setCustomerName] = useState(() => initialDraft?.customerName || '');
  const [customerPhone, setCustomerPhone] = useState(() => initialDraft?.customerPhone || '');
  const [patientQuery, setPatientQuery] = useState('');
  const debouncedPatientQuery = useDebounce(patientQuery, 300);
  const [customerOpen, setCustomerOpen] = useState(true);
  const [customerMode, setCustomerMode] = useState<'existing' | 'walk_in'>(
    () => initialDraft?.customerMode || 'existing',
  );

  /* ── catalog browsing ── */
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [activeCategory, setActiveCategory] = useState<string>(() => params.get('categoryId') || 'ALL');
  const [page, setPage] = useState(() => {
    const parsed = parseInt(params.get('page') || '1', 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  });

  /* ── cart / payment ── */
  const [cart, setCart] = useState<Map<string, CartLine>>(() => {
    const restored = new Map<string, CartLine>();
    (initialDraft?.cart || []).forEach((line) => {
      if (!line?.drug?.id) return;
      restored.set(line.drug.id, {
        drug: line.drug,
        quantity: Number(line.quantity) || 1,
        unitPrice: Number(line.unitPrice) || 0,
      });
    });
    return restored;
  });
  const [saleDiscountPercent, setSaleDiscountPercent] = useState(
    () => initialDraft?.saleDiscountPercent || '0',
  );
  const [payments, setPayments] = useState<PaymentLine[]>(() =>
    initialDraft?.payments && initialDraft.payments.length > 0
      ? initialDraft.payments
      : [{ method: 'cash', amount: '', reference: '' }],
  );
  const [notes, setNotes] = useState(() => initialDraft?.notes || '');

  /* ── feedback ── */
  const [formError, setFormError] = useState<string | null>(null);
  const [createdSale, setCreatedSale] = useState<any>(null);

  const drugFilters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      categoryId: activeCategory === 'ALL' ? undefined : activeCategory,
    }),
    [debouncedSearch, activeCategory],
  );

  const { data: drugsData, isLoading: drugsLoading, isFetching: drugsFetching, error: drugsError } =
    usePharmacyDrugs(page, ITEMS_PER_PAGE, drugFilters);
  const { data: categoriesData } = usePharmacyDrugCategories(1, 100);
  const { data: patientsData, isLoading: patientsLoading } = usePatients(1, 100, {
    isActive: true,
  });
  const { data: patientSearchData, isLoading: patientSearchLoading } = useSearchPatients(debouncedPatientQuery);
  const createSale = useCreatePharmacySale();
  const updateSale = useUpdatePharmacySale();
  const { data: saleData, isLoading: saleLoading, error: saleError } = usePharmacySale(saleId);

  const drugs = drugsData?.data || [];
  const meta = drugsData?.meta;
  const categories = categoriesData?.data || [];

  /* ── registered patients (same source as /patients) ── */
  const patientList = useMemo(() => {
    if (debouncedPatientQuery.length >= 2) return patientSearchData?.data || [];
    return patientsData?.data || [];
  }, [debouncedPatientQuery, patientSearchData, patientsData]);
  const patientsLoadingState = debouncedPatientQuery.length >= 2 ? patientSearchLoading : patientsLoading;
  const patientOptions = useMemo(() => {
    const options = patientList.map((patient: any) => {
      const name =
        joinFullName(patient) ||
        patient.name ||
        patient.registrationNumber ||
        'Patient';
      const meta = [patient.registrationNumber, patient.mobileNumber].filter(Boolean).join(' • ');
      return { value: patient.id, label: meta ? `${name} — ${meta}` : name };
    });
    if (selectedPatient && !options.some((option) => option.value === selectedPatient.id)) {
      const meta = [selectedPatient.registrationNumber, selectedPatient.mobileNumber].filter(Boolean).join(' • ');
      options.unshift({
        value: selectedPatient.id,
        label: meta ? `${selectedPatient.name} — ${meta}` : selectedPatient.name,
      });
    }
    return options;
  }, [patientList, selectedPatient]);

  /* ── keep category + page in the URL (stable across reloads) ── */
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (activeCategory !== 'ALL') newParams.set('categoryId', activeCategory);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, activeCategory, navigate, location.pathname]);

  /* ── persist the unsaved cart draft so it survives a reload ── */
  useEffect(() => {
    if (isEdit || draftClearedRef.current) {
      if (draftClearedRef.current) clearSaleDraft();
      return;
    }

    const hasDraft =
      cart.size > 0 ||
      notes.trim() !== '' ||
      saleDiscountPercent !== '0' ||
      payments.some((payment) => payment.amount !== '' || payment.reference !== '') ||
      patientId !== '' ||
      selectedPatient !== null ||
      customerName.trim() !== '' ||
      customerPhone.trim() !== '';

    if (!hasDraft) {
      clearSaleDraft();
      return;
    }

    saveSaleDraft({
      cart: Array.from(cart.values()),
      saleDiscountPercent,
      payments,
      notes,
      patientId,
      selectedPatient,
      customerName,
      customerPhone,
      customerMode,
    });
  }, [
    isEdit,
    cart,
    notes,
    saleDiscountPercent,
    payments,
    patientId,
    selectedPatient,
    customerName,
    customerPhone,
    customerMode,
  ]);

  /* ── derived totals ── */
  const cartLines = useMemo(() => Array.from(cart.values()), [cart]);
  const subtotal = useMemo(
    () => round(cartLines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0)),
    [cartLines],
  );
  const saleDiscountPercentValue = round(Math.max(0, Number(saleDiscountPercent) || 0));
  const saleDiscountValue = round((subtotal * saleDiscountPercentValue) / 100);
  const netTotal = round(Math.max(0, subtotal - saleDiscountValue));
  const paidTotal = useMemo(
    () => round(payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)),
    [payments],
  );
  const dueAmount = round(Math.max(0, netTotal - paidTotal));
  const changeDue = round(Math.max(0, paidTotal - netTotal));
  const itemCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);
  const isSaving = createSale.isPending || updateSale.isPending;

  /* ── cart actions ── */
  const addDrug = (drug: PharmacyDrug) => {
    if (Number(drug.currentStock ?? 0) <= 0) return;
    setCart((prev) => {
      if (prev.has(drug.id)) return prev;
      const next = new Map(prev);
      next.set(drug.id, {
        drug,
        quantity: 1,
        unitPrice: Number(drug.price) || 0,
      });
      return next;
    });
  };

  // Keyboard navigation for the catalog: Up/Down to move, Enter to add to cart —
  // matching the diagnostic receipt "add to cart" behaviour.
  const drugNav = useListKeyboardNav({
    itemCount: drugs.length,
    onSelect: (index) => {
      const drug = drugs[index];
      if (!drug) return;
      if (cart.has(drug.id)) return;
      if (Number(drug.currentStock ?? 0) <= 0) return;
      addDrug(drug);
    },
    isDisabled: (index) => {
      const drug = drugs[index];
      if (!drug) return true;
      return cart.has(drug.id) || Number(drug.currentStock ?? 0) <= 0;
    },
    resetKey: `${debouncedSearch}|${activeCategory}|${page}`,
  });

  const removeDrug = (drugId: string) => {
    setCart((prev) => {
      const next = new Map(prev);
      next.delete(drugId);
      return next;
    });
  };

  const updateLine = (drugId: string, patch: Partial<CartLine>) => {
    setCart((prev) => {
      const existing = prev.get(drugId);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(drugId, { ...existing, ...patch });
      return next;
    });
  };

  const clearCart = () => setCart(new Map());

  /* ── payment actions ── */
  const updatePayment = (index: number, patch: Partial<PaymentLine>) => {
    setPayments((prev) => prev.map((payment, i) => (i === index ? { ...payment, ...patch } : payment)));
  };
  const addPaymentLine = () =>
    setPayments((prev) => [...prev, { method: 'cash', amount: '', reference: '' }]);
  const removePaymentLine = (index: number) =>
    setPayments((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  const payFull = () =>
    setPayments((prev) => {
      const next = prev.length > 0 ? [...prev] : [{ method: 'cash' as const, amount: '', reference: '' }];
      next[0] = { ...next[0], amount: netTotal > 0 ? String(netTotal) : '' };
      return next;
    });

  /* ── patient actions ── */
  const selectPatient = (patient: any) => {
    const name =
      joinFullName(patient) ||
      patient.name ||
      patient.registrationNumber ||
      'Patient';
    setSelectedPatient({
      id: patient.id,
      name,
      registrationNumber: patient.registrationNumber,
      mobileNumber: patient.mobileNumber,
    });
    setPatientId(patient.id);
    setPatientQuery('');
    setFormError(null);
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setPatientId('');
    setPatientQuery('');
  };

  const handlePatientChange = (id: string) => {
    if (!id) {
      clearPatient();
      return;
    }
    const patient = patientList.find((item: any) => item.id === id);
    if (patient) selectPatient(patient);
  };

  const switchCustomerMode = (mode: 'existing' | 'walk_in') => {
    setCustomerMode(mode);
    setFormError(null);
    if (mode === 'existing') {
      setCustomerName('');
      setCustomerPhone('');
    } else {
      clearPatient();
    }
  };

  /* ── hydrate the form when editing an existing sale ── */
  const [hydratedSaleId, setHydratedSaleId] = useState<string | null>(null);
  useEffect(() => {
    const sale = saleData?.data;
    if (!isEdit || !sale || hydratedSaleId === sale.id) return;

    if (sale.patient) {
      const name =
        joinFullName(sale.patient) ||
        sale.patient.registrationNumber ||
        'Patient';
      setSelectedPatient({
        id: sale.patient.id || sale.patientId || '',
        name,
        registrationNumber: sale.patient.registrationNumber,
        mobileNumber: sale.patient.mobileNumber,
      });
      setPatientId(sale.patient.id || sale.patientId || '');
      setCustomerMode('existing');
    } else {
      setCustomerName(sale.customerName || '');
      setCustomerPhone(sale.customerPhone || '');
      setCustomerMode('walk_in');
    }

    const nextCart = new Map<string, CartLine>();
    (sale.items || []).forEach((item: any) => {
      const drug =
        item.drug ||
        ({
          id: item.drugId || item.id,
          name: item.itemName,
          itemType: item.itemType,
          price: Number(item.unitPrice) || 0,
          isActive: true,
        } as unknown as PharmacyDrug);
      nextCart.set(drug.id, {
        drug,
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
      });
    });
    setCart(nextCart);

    const hydratedSubtotal = Array.from(nextCart.values()).reduce(
      (sum, line) => sum + line.quantity * line.unitPrice,
      0,
    );
    const storedDiscount = Math.max(0, Number(sale.discountAmount || 0));
    const storedPercent = hydratedSubtotal > 0 ? round((storedDiscount / hydratedSubtotal) * 100) : 0;
    setSaleDiscountPercent(String(storedPercent));

    setPayments(
      sale.payments && sale.payments.length > 0
        ? sale.payments.map((payment: any) => ({
            method: payment.method,
            amount: String(payment.amount ?? ''),
            reference: payment.reference || '',
          }))
        : [{ method: 'cash', amount: '', reference: '' }],
    );
    setNotes(sale.notes || '');
    setHydratedSaleId(sale.id);
  }, [isEdit, saleData, hydratedSaleId]);

  const resetForm = () => {
    draftClearedRef.current = false;
    setCart(new Map());
    setSaleDiscountPercent('0');
    setPayments([{ method: 'cash', amount: '', reference: '' }]);
    setNotes('');
    setPatientId('');
    setSelectedPatient(null);
    setPatientQuery('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerMode('existing');
    setCreatedSale(null);
    setFormError(null);
  };

  const printReceipt = (sale: any) => {
    if (!sale) return;
    void printPharmacySaleReceipt(sale);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (cartLines.length === 0) {
      setFormError('Add at least one item to the sale');
      return;
    }
    if (!patientId && !customerName.trim()) {
      setFormError('Select a patient or enter a customer name for a walk-in sale');
      return;
    }
    if (saleDiscountPercentValue > 100) {
      setFormError('Discount percentage cannot exceed 100%');
      return;
    }
    if (paidTotal > netTotal) {
      setFormError('Amount paid cannot exceed the sale total');
      return;
    }
    // Editing an existing sale reverses its original issue before re-issuing, so
    // the per-line stock guard only applies to brand-new sales.
    if (!isEdit) {
      const overStock = cartLines.find(
        (line) => line.quantity > Number(line.drug.currentStock ?? 0),
      );
      if (overStock) {
        const available = Number(overStock.drug.currentStock ?? 0);
        setFormError(
          `Not enough stock for ${overStock.drug.name}. Available: ${available} ${overStock.drug.unitOfMeasure ?? ''}`.trim(),
        );
        return;
      }
    }

    const payload = {
      patientId: patientId || undefined,
      customerName: patientId ? undefined : customerName.trim() || undefined,
      customerPhone: patientId ? undefined : customerPhone.trim() || undefined,
      discountAmount: saleDiscountValue,
      notes: notes.trim() || undefined,
      items: cartLines.map((line) => ({
        drugId: line.drug.id,
        itemName: line.drug.name,
        itemType: line.drug.itemType,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
      payments: payments
        .filter((payment) => Number(payment.amount) > 0)
        .map((payment) => ({
          method: payment.method,
          amount: Number(payment.amount),
          reference: payment.reference.trim() || undefined,
        })),
    };

    try {
      const result = isEdit
        ? await updateSale.mutateAsync({ id: saleId!, data: payload })
        : await createSale.mutateAsync(payload);
      // Sale is complete: drop the temporary cart draft.
      if (!isEdit) {
        draftClearedRef.current = true;
        clearSaleDraft();
      }
      setCreatedSale(result.data);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to record the sale. Please check the items and payment.');
    }
  };

  const DrugRow = ({ drug, index }: { drug: PharmacyDrug; index: number }) => {
    const inCart = cart.has(drug.id);
    const price = Number(drug.price || 0);
    const stock = Number(drug.currentStock ?? 0);
    const outOfStock = stock <= 0;
    const disabled = inCart || outOfStock;
    const highlighted = index === drugNav.highlightedIndex && !disabled;
    return (
      <button
        type="button"
        data-nav-index={index}
        onMouseEnter={() => drugNav.setHighlightedIndex(index)}
        onClick={() => !disabled && addDrug(drug)}
        disabled={disabled}
        className={`w-full text-left flex items-center justify-between px-4 py-3 transition-colors border-b border-border last:border-0 ${
          inCart
            ? 'bg-success/5 cursor-default'
            : outOfStock
              ? 'opacity-60 cursor-not-allowed'
              : highlighted
                ? 'bg-background ring-1 ring-inset ring-primary/40 cursor-pointer'
                : 'hover:bg-background cursor-pointer'
        }`}
      >
        <div className="min-w-0 flex items-center gap-3">
          {inCart ? (
            <CheckCircle2 size={16} className="text-success shrink-0" />
          ) : (
            <div className="w-4 h-4 rounded border border-border shrink-0" />
          )}
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${inCart ? 'text-success' : 'text-text'}`}>
              {drug.name}
            </p>
            <p className="text-xs text-text-muted truncate">
              {[drug.genericName?.name, drug.company?.name].filter(Boolean).join(' • ') || drug.drugCode}
            </p>
            <p className="text-[0.6875rem] text-text-muted">
              {[drug.itemType === 'ACCESSORY' ? 'Accessory' : null, drug.form, drug.strength, drug.unitOfMeasure]
                .filter(Boolean)
                .join(' • ')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <div className="text-right leading-tight">
            <span className="text-sm font-bold text-primary whitespace-nowrap">{money(price)}</span>
            <p className={`text-[0.6875rem] whitespace-nowrap ${outOfStock ? 'text-danger' : 'text-text-muted'}`}>
              {outOfStock ? 'Out of stock' : `${stock} ${drug.unitOfMeasure} in stock`}
            </p>
          </div>
          {!disabled && (
            <span className="text-xs bg-primary text-white px-2 py-1 rounded whitespace-nowrap">Add</span>
          )}
        </div>
      </button>
    );
  };

  const renderDrugList = () => {
    if (drugsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={22} className="animate-spin text-primary" />
        </div>
      );
    }
    if (drugs.length === 0) {
      return (
        <div className="text-center py-10">
          <Package className="w-8 h-8 text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-muted">No items found</p>
          <p className="text-xs text-text-muted mt-1">Try a different search or category</p>
        </div>
      );
    }
    return (
      <div ref={drugNav.listRef} className="border border-border rounded-lg overflow-hidden">
        {drugs.map((drug: any, index: number) => (
          <DrugRow key={drug.id} drug={drug} index={index} />
        ))}
      </div>
    );
  };

  if (isEdit && saleLoading) {
    return (
      <PharmacyLayout
        header={
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pharmacy')}
              className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-text">Edit Counter Sale</h1>
          </div>
        }
      >
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </PharmacyLayout>
    );
  }

  if (isEdit && (saleError || !saleData?.data)) {
    return (
      <PharmacyLayout
        header={
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pharmacy')}
              className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-text">Edit Counter Sale</h1>
          </div>
        }
      >
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load this sale. Please go back and try again.</span>
        </div>
      </PharmacyLayout>
    );
  }

  return (
    <PharmacyLayout
      header={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pharmacy')}
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              {isEdit ? (
                <Pencil className="w-6 h-6 text-primary" />
              ) : (
                <ShoppingCart className="w-6 h-6 text-primary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text">
                {isEdit ? 'Edit Counter Sale' : 'New Counter Sale'}
              </h1>
              <p className="text-sm text-text-muted">
                {isEdit
                  ? 'Update items, payment and stock for this sale'
                  : 'Add items, take payment and deduct stock'}
              </p>
            </div>
          </div>
        </div>
      }
      action={
        isEdit && saleData?.data ? (
          <button
            onClick={() => printReceipt(saleData.data)}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Printer size={16} />
            Print Receipt
          </button>
        ) : undefined
      }
    >
      <div className="space-y-6 w-full">
        {formError && (
          <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{formError}</span>
          </div>
        )}

        {/* Browse + Cart */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-surface rounded-xl border border-border p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Package size={18} className="text-primary" />
                <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Available Items</h2>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  onKeyDown={drugNav.onKeyDown}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  placeholder="Search by name, generic, company or code..."
                />
                {(searchQuery || drugsFetching) && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {drugsFetching && <Loader2 size={14} className="animate-spin text-text-muted" />}
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setPage(1);
                        }}
                        className="text-text-muted hover:text-text"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                <button
                  onClick={() => {
                    setActiveCategory('ALL');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeCategory === 'ALL'
                      ? 'bg-primary text-white'
                      : 'bg-background border border-border text-text-muted hover:text-text'
                  }`}
                >
                  All Categories
                </button>
                {categories.map((category: any) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activeCategory === category.id
                        ? 'bg-primary text-white'
                        : 'bg-background border border-border text-text-muted hover:text-text'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              <div className="p-6 space-y-4">
                {drugsError && (
                  <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span>Failed to load items. Please try again.</span>
                  </div>
                )}
                {renderDrugList()}
              </div>
              {meta && meta.totalPages > 1 && (
                <Pagination
                  page={meta.page}
                  totalPages={meta.totalPages}
                  total={meta.total}
                  limit={meta.limit}
                  onPageChange={setPage}
                  itemName="items"
                />
              )}
            </div>
          </div>

          {/* Cart / Checkout */}
          <div className="xl:col-span-1">
            <div className="sticky top-6 bg-surface rounded-xl border border-border p-6 space-y-5">
              <div className="flex items-center gap-2">
                <ShoppingCart size={18} className="text-primary" />
                <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Sale Cart</h2>
              </div>

              {cartLines.length === 0 ? (
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <ShoppingCart className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-muted font-medium">Cart is empty</p>
                  <p className="text-xs text-text-muted mt-1">Browse items on the left to add</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                  {cartLines.map((line) => {
                    const lineGross = round(line.quantity * line.unitPrice);
                    const canDecrease = line.quantity > 1;
                    const available = Number(line.drug.currentStock ?? 0);
                    const atMax = !isEdit && available > 0 && line.quantity >= available;
                    return (
                      <div
                        key={line.drug.id}
                        className="group relative flex flex-wrap items-center gap-x-2 gap-y-1.5 border border-border rounded-xl px-3 py-2 bg-background/60 hover:bg-background hover:border-primary/30 transition-colors"
                      >
                        <div className="min-w-0 flex-1 basis-28">
                          <p className="text-[0.8125rem] font-semibold text-text truncate leading-tight">
                            {line.drug.name}
                          </p>
                          <p className="text-[0.625rem] text-text-muted truncate leading-tight mt-0.5">
                            {[line.drug.itemType === 'ACCESSORY' ? 'Accessory' : null, line.drug.form, line.drug.strength]
                              .filter(Boolean)
                              .join(' • ') || '—'}
                          </p>
                          <p
                            className={`text-[0.625rem] truncate leading-tight ${atMax ? 'text-warning' : 'text-text-muted'}`}
                          >
                            Available: {available} {line.drug.unitOfMeasure}
                            {atMax ? ' (max)' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-auto">
                          <div className="flex items-center rounded-lg border border-border bg-surface overflow-hidden">
                            <button
                              type="button"
                              onClick={() => updateLine(line.drug.id, { quantity: Math.max(1, line.quantity - 1) })}
                              disabled={!canDecrease}
                              className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-primary hover:bg-primary/5 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                              title="Decrease quantity"
                            >
                              <Minus size={12} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              max={!isEdit && available > 0 ? available : undefined}
                              value={line.quantity}
                              onChange={(e) => {
                                const parsed = Math.max(1, parseInt(e.target.value, 10) || 1);
                                const capped = !isEdit && available > 0 ? Math.min(parsed, available) : parsed;
                                updateLine(line.drug.id, { quantity: capped });
                              }}
                              className="w-9 h-7 text-center text-xs font-medium bg-transparent text-text focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              onClick={() => updateLine(line.drug.id, { quantity: line.quantity + 1 })}
                              disabled={atMax}
                              className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-primary hover:bg-primary/5 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                              title={atMax ? 'No more stock available' : 'Increase quantity'}
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <div className="text-right leading-tight min-w-[52px]">
                            <p className="text-sm font-bold text-text">{money(lineGross)}</p>
                            <p className="text-[0.625rem] text-text-muted">{money(line.unitPrice)} each</p>
                          </div>
                          <button
                            onClick={() => removeDrug(line.drug.id)}
                            className="p-1 -mr-1 rounded-lg text-text-muted hover:bg-danger/10 hover:text-danger transition-colors shrink-0"
                            title="Remove"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {cartLines.length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">{itemCount} item(s)</span>
                  <button onClick={clearCart} className="text-xs text-danger hover:underline font-medium">
                    Clear all
                  </button>
                </div>
              )}

              <div className="h-px bg-border" />

              {/* Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Subtotal</span>
                  <span className="text-text font-medium">{money(subtotal)}</span>
                </div>
                {saleDiscountValue > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Discount ({saleDiscountPercentValue}%)</span>
                    <span className="text-text font-medium">-{money(saleDiscountValue)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-base pt-1 border-t border-border">
                  <span className="text-text font-semibold">Total</span>
                  <span className="text-text font-bold">{money(netTotal)}</span>
                </div>
              </div>

              {/* Customer */}
              <div className="pt-1 pb-4 border-b border-border">
                <button
                  type="button"
                  onClick={() => setCustomerOpen((open) => !open)}
                  className="w-full flex items-center justify-between gap-2 text-left"
                >
                  <span className="flex items-center gap-2">
                    <User size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-text">Customer</span>
                    {!customerOpen && (selectedPatient || customerName.trim()) && (
                      <span className="text-xs text-text-muted truncate">
                        {selectedPatient ? selectedPatient.name : customerName.trim()}
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-text-muted transition-transform ${customerOpen ? '' : '-rotate-90'}`}
                  />
                </button>
                {customerOpen && (
                  <div className="space-y-3 mt-3">
                    <div className="flex items-center gap-1 p-1 rounded-lg bg-background border border-border">
                      <button
                        type="button"
                        onClick={() => switchCustomerMode('existing')}
                        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          customerMode === 'existing'
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-text-muted hover:text-text'
                        }`}
                      >
                        Existing
                      </button>
                      <button
                        type="button"
                        onClick={() => switchCustomerMode('walk_in')}
                        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          customerMode === 'walk_in'
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-text-muted hover:text-text'
                        }`}
                      >
                        Walk in
                      </button>
                    </div>

                    {customerMode === 'existing' ? (
                      selectedPatient ? (
                        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background/50">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <User size={16} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-text truncate">{selectedPatient.name}</p>
                              <p className="text-xs text-text-muted truncate">
                                {[selectedPatient.registrationNumber, selectedPatient.mobileNumber]
                                  .filter(Boolean)
                                  .join(' • ') || 'Registered patient'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={clearPatient}
                            className="text-xs text-text-muted hover:text-danger transition-colors shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <SearchableSelect
                          label="Find registered patient"
                          labelClassName="block text-xs font-medium text-text-muted mb-1"
                          value={patientId}
                          onChange={handlePatientChange}
                          options={patientOptions}
                          placeholder="Search by name, mobile or reg. no..."
                          loading={patientsLoadingState}
                          onQueryChange={setPatientQuery}
                        />
                      )
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-text-muted mb-1">Walk-in name</label>
                          <input
                            type="text"
                            value={customerName}
                            onChange={(e) => {
                              setCustomerName(e.target.value);
                              setFormError(null);
                            }}
                            placeholder="Customer name"
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-muted mb-1">Mobile</label>
                          <input
                            type="text"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            placeholder="Optional"
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Banknote size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-text">Payment</span>
                  </div>
                  <button onClick={payFull} className="text-xs text-primary hover:underline font-medium">
                    Pay full
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-text-muted">Discount (%)</span>
                  <div className="relative">
                    <ValidatedNumberInput
                      max={100}
                      value={saleDiscountPercent}
                      onChange={(v) => setSaleDiscountPercent(String(v))}
                      placeholder="0"
                      className="w-28 px-2 py-1.5 pr-6 text-xs rounded border border-border bg-background text-text text-right focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-text-muted pointer-events-none">
                      %
                    </span>
                  </div>
                </div>
                {saleDiscountValue > 0 && (
                  <div className="flex items-center justify-between text-xs -mt-1">
                    <span className="text-text-muted">Discount amount</span>
                    <span className="text-text font-medium">-{money(saleDiscountValue)}</span>
                  </div>
                )}

                {payments.map((payment, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <select
                        value={payment.method}
                        onChange={(e) => updatePayment(index, { method: e.target.value as PharmacySalePaymentMethod })}
                        className="w-full px-2 py-1.5 text-xs rounded border border-border bg-background text-text focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-4">
                      <ValidatedNumberInput
                        value={payment.amount}
                        onChange={(v) => updatePayment(index, { amount: v ? String(v) : '' })}
                        placeholder="Amount"
                        className="w-full px-2 py-1.5 text-xs rounded border border-border bg-background text-text focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={payment.reference}
                        onChange={(e) => updatePayment(index, { reference: e.target.value })}
                        placeholder="Ref"
                        className="w-full px-2 py-1.5 text-xs rounded border border-border bg-background text-text focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePaymentLine(index)}
                      disabled={payments.length <= 1}
                      className="col-span-1 p-1.5 rounded text-text-muted hover:text-danger disabled:opacity-30"
                      title="Remove payment"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}

                <button
                  onClick={addPaymentLine}
                  className="text-xs text-primary hover:underline font-medium flex items-center gap-1"
                >
                  <Plus size={12} /> Add payment method
                </button>

                <div className="rounded-lg border border-border bg-background/50 p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Total</span>
                    <span className="text-text font-semibold">{money(netTotal)}</span>
                  </div>
                  {saleDiscountValue > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Discount applied ({saleDiscountPercentValue}%)</span>
                      <span className="text-text font-medium">-{money(saleDiscountValue)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Paid</span>
                    <span className="text-text font-medium">{money(paidTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">{changeDue > 0 ? 'Change' : 'Due'}</span>
                    <span className={`font-semibold ${dueAmount > 0 ? 'text-warning' : 'text-success'}`}>
                      {money(changeDue > 0 ? changeDue : dueAmount)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Note</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-y"
                  placeholder="Optional note for this sale..."
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={cartLines.length === 0 || isSaving}
                className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Complete Sale'}
              </button>
              {(!patientId && !customerName.trim()) && (
                <p className="text-[0.6875rem] text-text-muted text-center -mt-2">
                  Select a patient or enter a walk-in name to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {createdSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm space-y-4 shadow-xl mx-4">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-bold text-text">
                {isEdit ? 'Sale Updated' : 'Sale Recorded'}
              </h3>
              <p className="text-sm text-text-muted">
                {createdSale.saleNumber} • {createdSale.items?.length || cartLines.length} item(s)
              </p>
              <div className="w-full rounded-lg border border-border bg-background/50 p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Total</span>
                  <span className="text-text font-semibold">{money(Number(createdSale.totalAmount))}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Paid</span>
                  <span className="text-text font-medium">{money(Number(createdSale.paidAmount))}</span>
                </div>
                {Number(createdSale.dueAmount) > 0 ? (
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Due</span>
                    <span className="text-warning font-semibold">{money(Number(createdSale.dueAmount))}</span>
                  </div>
                ) : (
                  changeDue > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Change</span>
                      <span className="text-success font-semibold">{money(changeDue)}</span>
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-center text-sm text-text-muted">
                Do you want to print the customer receipt?
              </p>
              <button
                onClick={() => printReceipt(createdSale)}
                className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-bold flex items-center justify-center gap-2"
              >
                <Printer size={18} />
                Print Receipt
              </button>
              <div className="flex items-center gap-2">
                {!isEdit && (
                  <button
                    onClick={resetForm}
                    className="flex-1 px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium"
                  >
                    New Sale
                  </button>
                )}
                <button
                  onClick={() => navigate('/pharmacy')}
                  className="flex-1 px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium"
                >
                  View Sales
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PharmacyLayout>
  );
}
